import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';

const MAX_COLOR_WAYS = 9;
const DEFAULT_COLOR_RUNS = 4;

// ColorWay management
export default class extends Controller {
  static targets = ['listing', 'btn', 'colorWays', 'colorRuns'];

  static values = {
    artworkId: Number,
    discipline: String,
  };

  connect() {
    this.cwIndex = this.colorWaysTargets.length;
    this._onTypeChanged = this.onTypeChanged.bind(this);
    document.addEventListener('produceable-type:changed', this._onTypeChanged);
  }

  disconnect() {
    document.removeEventListener(
      'produceable-type:changed',
      this._onTypeChanged
    );
  }

  onTypeChanged() {
    // Every discipline may add color ways - Digital defaults to one but is not capped.
    this.btnTarget.classList.remove('d-none');
  }

  newColorWay(e) {
    e.preventDefault();

    return fetch(`/color_ways/new?${this.colorWayParams()}`, {
      headers: {
        Accept: 'text/vnd.turbo-stream.html',
      },
    })
      .then((resp) => resp.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
        this.cwIndex += 1; // ensure any new nested set has new index
        requestAnimationFrame(() => {
          if (this.colorWaysTargets.length >= MAX_COLOR_WAYS) {
            this.btnTarget.classList.add('d-none');
          }
        });
      });
  }

  destroyColorWay(event) {
    event.stopPropagation();
    event.preventDefault();
    const delBtn = event.currentTarget;
    const colorWayIndex = this.colorWaysTargets.findIndex((t) =>
      t.contains(delBtn)
    );
    const colorWayNode = this.colorWaysTargets[colorWayIndex];

    if (!delBtn.dataset.colorWayIdValue) {
      this.listingTarget.removeChild(colorWayNode);
      if (this.colorWaysTargets.length < MAX_COLOR_WAYS) {
        this.btnTarget.classList.remove('d-none');
      }
    } else {
      // disable value inputs for persisted color ways
      const inputs = colorWayNode.querySelectorAll(
        'button,input[type="text"],input[type="checkbox"]'
      );
      Array.prototype.forEach.call(inputs, (el) => {
        el.setAttribute('disabled', 'disabled');
      });
      // flag hidden destroy field
      colorWayNode
        .querySelector('input[type="hidden"]')
        .setAttribute('value', true);
    }
  }

  colorWayParams() {
    const params = new URLSearchParams();
    const discipline =
      this.artworkIdValue === 0
        ? document.querySelector('select[name="artwork[produceable_type]"]')
            ?.value
        : this.disciplineValue;
    // Digital is one ink pass by default, so it opens with a single color run.
    let colorRunCount = discipline === 'Digital' ? 1 : DEFAULT_COLOR_RUNS;
    if (this.colorWaysTargets.length > 0) {
      // Match the first color way's current size. A persisted run that was deleted
      // stays in the DOM flagged for destruction, so it must not be counted.
      colorRunCount = Array.from(
        this.colorWaysTargets[0].querySelectorAll(
          'div[data-color-run-target="colorRuns"]'
        )
      ).filter(
        (run) => run.querySelector('input[name*=destroy]')?.value !== 'true'
      ).length;
      // Never open a color way with no runs to fill in.
      colorRunCount = Math.max(colorRunCount, 1);
    }
    params.append('artwork_id', this.artworkIdValue);
    params.append('color_runs_count', colorRunCount);
    params.append('color_ways_index', this.cwIndex);
    params.append('color_ways_count', this.colorWaysTargets.length);
    params.append('produceable_type', discipline);

    return params;
  }
}
